import {Group} from '@visx/group';
import {PieArcDatum, ProvidedProps} from '@visx/shape/lib/shapes/Pie';
import React from 'react';
import {useSpring, animated} from 'react-spring';

import {DataFrameRow} from 'data-frames/DataFrameTypes';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY_DARKER} from 'ui-library/Colors';

import {LABEL_PADDING, PieChartConfig} from '../PieChart';
import {LABEL_FONT_SIZE, LABEL_HEIGHT} from './utils/Theme';
import truncateToMaxLengthLabel from './utils/truncateLabels';

const MIN_ANGLE_TO_DISPLAY_LABEL = 0.2;
const TRANSITION_PERCENTAGE = 0.1;
export const FADE_OPACITY = 0.35;

export function calculateLabelPosition(
  centroidX: number,
  centroidY: number,
  radius: number,
  hasMultilineLabel: boolean
): {labelX: number; labelY: number} {
  // Calculate position of label outside of the pie
  const distanceFromCenter = Math.sqrt(centroidX * centroidX + centroidY * centroidY);
  const labelPadding = hasMultilineLabel ? LABEL_PADDING + 5 : LABEL_PADDING;
  const labelX = (centroidX / distanceFromCenter) * (radius + labelPadding);
  const labelY = (centroidY / distanceFromCenter) * (radius + labelPadding);
  return {labelX, labelY};
}

export function calculateHoverTranslation(
  centroidX: number,
  centroidY: number,
  radius: number
): {x: number; y: number} {
  const distanceFromCenter = Math.sqrt(centroidX * centroidX + centroidY * centroidY);
  const x = (centroidX / distanceFromCenter) * (radius * TRANSITION_PERCENTAGE);
  const y = (centroidY / distanceFromCenter) * (radius * TRANSITION_PERCENTAGE);

  return {x, y};
}

export function getLabelAnchor(arc: PieArcDatum<DataFrameRow>): string {
  // Position the label based on its rotational distance from the midpoint
  const angleFromMidpoint = Math.abs((arc.endAngle + arc.startAngle) / 2 - Math.PI);
  const isNearMidpoint = angleFromMidpoint < 0.1;
  const nonMidpointTextAnchor = (arc.endAngle + arc.startAngle) / 2 > Math.PI ? 'end' : 'start';
  const textAnchor = isNearMidpoint ? 'middle' : nonMidpointTextAnchor;
  return textAnchor;
}

interface PieChartSliceProps {
  arc: PieArcDatum<DataFrameRow>;
  color: string;
  config: PieChartConfig;
  getNumericLabel: (
    value: number,
    totalPieValue: number,
    showValue: boolean,
    showPercentage: boolean
  ) => string;
  estimateMaxLabelLength: (labelX: number) => number;
  isFaded: boolean;
  isHovered: boolean;
  onHover: (arc?: PieArcDatum<DataFrameRow>) => void;
  pie: ProvidedProps<DataFrameRow>;
  sliceNameAccessor: (slice: DataFrameRow) => string;
  totalPieValue: number;
  valueAccessor: (slice: DataFrameRow) => number;
}

export default function PieChartSlice({
  arc,
  color,
  config: {
    sliceLabels: {showSliceName, showValue, showPercentage},
  },
  getNumericLabel,
  estimateMaxLabelLength,
  isFaded,
  isHovered,
  onHover,
  pie,
  sliceNameAccessor,
  totalPieValue,
  valueAccessor,
}: PieChartSliceProps): JSX.Element {
  const [centroidX, centroidY] = pie.path.centroid(arc);
  const radius = pie.path.outerRadius()(arc);
  const {x: translateX, y: translateY} = calculateHoverTranslation(centroidX, centroidY, radius);

  const {translate} = useSpring<{translate: Array<number>}>({
    translate: isHovered ? [translateX, translateY] : [0, 0],
    config: {duration: 50},
  });

  const label = sliceNameAccessor(arc.data);
  const value = valueAccessor(arc.data);

  // If totalPieValue === value, then this is the only slice in the pie and we can skip the animation
  const shouldAnimate = value < totalPieValue;
  const hasSpaceForLabel = arc.endAngle - arc.startAngle >= MIN_ANGLE_TO_DISPLAY_LABEL;
  const labelAnchor = getLabelAnchor(arc);
  const {labelX, labelY} = calculateLabelPosition(centroidX, centroidY, radius, showValue || showPercentage);

  const arcPath = pie.path(arc);

  return (
    <>
      <animated.g
        {...getTestProps({component: 'PieChartSlice', element: 'root'})}
        transform={shouldAnimate ? translate.to((x, y) => `translate(${x}, ${y})`) : undefined}
        opacity={isFaded ? FADE_OPACITY : 1}
      >
        <path
          d={arcPath ?? undefined}
          fill={color}
          {...getTestProps({component: 'PieChartSlice', element: 'dataPath'})}
        />
        {hasSpaceForLabel && (
          <Group top={labelY} left={labelX}>
            {showSliceName && (
              <text fill={GRAY_DARKER} fontSize={LABEL_FONT_SIZE} textAnchor={labelAnchor}>
                {truncateToMaxLengthLabel(String(label), estimateMaxLabelLength(labelX))}
              </text>
            )}
            {(showValue || showPercentage) && (
              <text
                y={showSliceName ? LABEL_HEIGHT : 0}
                fill={GRAY_DARKER}
                fontSize={LABEL_FONT_SIZE}
                textAnchor={labelAnchor}
              >
                {getNumericLabel(value, totalPieValue, showValue, showPercentage)}
              </text>
            )}
          </Group>
        )}
      </animated.g>
      <path
        d={arcPath ?? undefined}
        fill="transparent"
        {...getTestProps({component: 'PieChartSlice', element: 'hitAreaPath'})}
        onMouseEnter={() => {
          onHover(arc);
        }}
        onMouseLeave={() => {
          onHover();
        }}
      />
    </>
  );
}
